import type { SagaCond } from './saga.ts';
import type { SendaChapter, SendaLine } from './sendas.ts';
import { sec } from './units.ts';

/**
 * The Long Hunt's chapter 5, «La guarida» (docs/sendas/barbaro.md §3, §4, §5, §7): the black rock in the sea, the climb up
 * its wall with Ulla, the four mouths of its cave, the ash bed, the speech to the clan in the boats and the Grey Wolf
 * (in the den, on the summit with the boats closing the sea, the last run); the close on the rock, Bera on the White
 * Island as the boats go by, and the clan singing Sigrun right at the tail. Apart from the other chapters' file so the
 * path's branches join cleanly (docs/SENDAS.md, «Para no chocar»).
 */

/**
 * The numbers of the chapter (the sim's battle/roca*.ts, climb.ts, mouths.ts, ashbed.ts, graywolf.ts). Lives in seconds
 * of D, blows in bp of the hero's life (docs/SENDAS.md, «Calibración»).
 */
export const ROCA = {
  /**
   * Ulla beside the hero (battle/roca-ally.ts): she strikes what is nearest within `reach` every `every` for `hitCd`
   * hundredths of a second of D. Her horn (a tap: `horn.uses` in the climb, one a phase in the fight): what of the pack is within
   * `horn.reach` of her stands frozen `horn.scare`, and a howl it hears is cut. Ivar, on the summit: the same (`ivar`),
   * a step behind the hero (`follow`); with Sigrun's axe left in the ice he covers the hero, taking `coverBp` of the
   * Wolf's bites when he is at it too.
   */
  ulla: { every: sec(1), hitCd: 45, reach: 1150 },
  horn: { reach: 6500, scare: sec(3), uses: 1 },
  ivar: { every: sec(1.25), hitCd: 35, reach: 1250, speed: 130, follow: 1600, coverBp: 350 },
  /**
   * The climb (act 1, battle/climb.ts): up the ledges of the rock's wall to the cave's mouth (won within `reach` of it,
   * `settle` after). As the hero comes onto a ledge a band of the pack comes down onto it from the next one (`ledge`:
   * wolves, by ledge; the lowest's as the act begins), and holds the narrow ramp up until it is down; the pack also comes down on its own, `pack.n` every `pack.every` from `pack.first`, onto the
   * ledge above the hero. Soot crows off the nests, `crows.n` every `crows.every` from `crows.first`. The howler waits on
   * ledge `howler.at`: met, it rears and howls (`rear`, `howl`) every `every`, `first` after; `cutBp` of its life struck
   * while it does cuts it, else `raise` wolves.
   */
  climb: {
    reach: 1500,
    settle: sec(1),
    ledge: [5, 6, 7, 8, 8],
    pack: { first: sec(14), every: [sec(18), sec(22)] as [number, number], n: [2, 3] as [number, number] },
    crows: { first: sec(10), every: sec(18), n: 2 },
    howler: { d: 14, at: 3, first: sec(4), every: sec(22), rear: sec(3), howl: sec(2.5), cutBp: 1500, raise: 3, stun: sec(2) },
    wolfD: 5,
    crowD: 2,
    hpBp: 10000,
    dmgBp: 12000,
  },
  /**
   * The mouths of the rock (act 2, the puzzle, battle/mouths.ts; §4): four mouths down to the sea, each under an arch of
   * rock, cracked. A tap sends the hero to an arch (within `reach`): one blow there (`strike`), one crack; at `breaks`
   * the arch comes down over its mouth and every standing arch joined to it by a crack in the roof takes one more (and
   * on, as far as it goes). Won with `need` mouths shut and nothing come down for `settle`. Cubs up out of the open
   * mouths, `cubs.n` every `cubs.every` from `cubs.first` (round the open ones); wolves `wolves.n` every `wolves.every`.
   * The mouth without the Wolf's tracks is the pack's own (stones below, their lair): bringing its arch down flushes the
   * pack out through the roof's cracks all at once (`surge`: `wolves` and `cubs` round Ulla) and the attempt is lost
   * `surge.lost` after (2026-11-04: without it, striking blind won the act every time; docs/DECISIONS.md).
   */
  mouths: {
    reach: 1300,
    strike: sec(1),
    breaks: 3,
    need: 2,
    settle: sec(6),
    cubs: { first: sec(10), every: sec(20), n: [2, 3] as [number, number] },
    wolves: { first: sec(26), every: sec(30), n: 2 },
    surge: { wolves: 5, cubs: 6, every: sec(9), lost: sec(3) },
    cubD: 1,
    cubDmgBp: 3500,
    wolfD: 3,
    hpBp: 10000,
    dmgBp: 12000,
  },
  /**
   * The ash bed (act 3, battle/ashbed.ts): down from the cave's mouth through two chambers to the bed. Coming into a
   * chamber its band comes out of the side cracks (`bands`: cubs, wolves, by chamber; in two parts `part` apart); its
   * way on is shut (ash and bones) until the band is down. Won within `reach` of the bed, `settle` after.
   */
  bed: {
    reach: 1700,
    settle: sec(1),
    bands: [
      [14, 3],
      [18, 4],
      [12, 5],
    ] as [number, number][],
    part: sec(4),
    cubD: 1,
    cubDmgBp: 3500,
    wolfD: 5,
    hpBp: 10000,
    dmgBp: 21000,
  },
  /**
   * The Grey Wolf (act 4, battle/graywolf.ts; §5), `d` seconds of D, biting `biteBp` every `reload` within `reach`, at
   * `speed` a second fighting (1,2 of the hero's). Three phases:
   *
   * 1. In the den (down to `phase2Bp` of its life): each `fleeBp` lost it looks `look` at the open mouth nearest
   *    (marked) and runs for it at `flee` a second (each blow on the way `slowBp` slower, never under `minSlowBp`; each
   *    scar of the path's enemy `scarSlowBp` slower). The hero within `block` of it when it gets there, or `caught`
   *    blows on the way, cut it (it reels `stun`). Getting there it is under `under` and back with `healBp` more (with
   *    Sigrun's axe `axeHealBp`; by the mouth without its tracks, stones below, nothing), `rise` cubs up with it, and
   *    angrier: each time back its bite `diveBiteBp` harder (let it heal again and again and it wears the hero down). A slab with two cracks on its
   *    way gives under it: stuck `slab.stuck` (the hero coming onto a slab cracks it; a third time, it gives under the
   *    hero: `slab.fallBp`).
   * 2. On the summit (down to `phase3Bp`; `phase3ShutBp` with every mouth shut, which has it never flee in the den): up
   *    the crack in the roof; it flees to the summit's edges, the clan's boats under one of them (the next every
   *    `boats.every`; with the axe left in the ice the island's edge shut for good), Ivar fighting beside the hero.
   * 3. The last run: no fleeing, no howl, `huntBp` faster, a leap every `leap.last`. With the hero under `boat.heroBp`
   *    of its life it goes down the wall for Sten's boat (a blow on it every `boat.gnaw`) until `boat.caught` blows turn
   *    it or the hero is back over `boat.back`; not again for `boat.again`.
   *
   * Every `howl.every` (from `howl.first`) it bristles `howl.rear` and howls `howl.howl`; let through, `howl.raise`
   * (wolves, cubs) come. Cut by a pillar of the den's roof or a stone of the summit's edge brought down on it (the hero
   * at it, within `drop.reach`, strikes it `drop.strike`; it within `drop.r`), Ulla's horn, or `howl.cutBp` of its life:
   * it reels `howl.stun`, taking `howl.takenBp` more. Its leap every `leap.every`: the shadow where the hero stands,
   * `leap.warn` before; `leap.bp` within `leap.r`. Cubs out of the open mouths, `cubs.n` every `cubs.every` (the den).
   */
  wolf: {
    d: 320,
    biteBp: 850,
    reload: sec(3),
    reach: 1350,
    speed: 2160,
    flee: 1800,
    fleeBp: 1000,
    look: sec(3),
    block: 1800,
    caught: 30,
    slowBp: 800,
    minSlowBp: 5000,
    scarSlowBp: 1000,
    stun: sec(2),
    under: sec(8),
    healBp: 1500,
    axeHealBp: 800,
    rise: 0,
    diveBiteBp: 2000,
    phase2Bp: 6000,
    phase3Bp: 2500,
    phase3ShutBp: 4000,
    huntBp: 12500,
    howl: { first: sec(25), every: sec(40), rear: sec(3), howl: sec(2.5), cutBp: 150, stun: sec(2), takenBp: 5000, raise: [3, 4] as [number, number] },
    drop: { strike: sec(0.5), r: 2600, reach: 1300 },
    leap: { every: sec(12), last: sec(8), warn: sec(3), r: 1300, bp: 2500, air: sec(0.5) },
    slab: { stuck: sec(3), fallBp: 1500 },
    boats: { every: sec(25) },
    boat: { heroBp: 3000, gnaw: sec(1), caught: 4, again: sec(15), back: 5000 },
    cubs: { first: sec(12), every: sec(20), n: [3, 4] as [number, number] },
    cubD: 1,
    cubDmgBp: 3500,
    wolfD: 3,
    hpBp: 10000,
    dmgBp: 10000,
  },
};

const ln = (id: string, who: string, cond?: SagaCond): SendaLine => (cond ? { id, who, if: cond } : { id, who });

const TAKEN: SagaCond = { flag: 'axeTaken' };
const LEFT: SagaCond = { flag: 'axeLeft' };

/**
 * Chapter 5, «La guarida» (level 42): a black rock alone in the sea, the small boat coming in the dawn's fog with the
 * clan's far behind (the cinematic); at its foot, Ulla jumped from the clan's first boat into Sten's; the climb up its
 * wall with Ulla and her horn; the four mouths of its cave (Ivar hurt against a wall); the campfire at the cave's mouth
 * with Ulla and Ivar; down through the cave to the ash bed and the carved wooden boar; Ulla's speech to the clan in the
 * boats; the Grey Wolf; the close on the rock as its ash goes with the wind, Bera on the White Island as the boats go
 * by, and the clan singing Sigrun right at the tail by night.
 */
export const ROCK_CHAPTER: SendaChapter = {
  id: 'rock',
  closedBy: 'ulla',
  piece: 'sigrunPelt',
  call: true,
  // Lost to the Grey Wolf, a closed chapter opens again at its fight: the climb, the mouths and the bed stay won.
  keep: 'wolf',
  acts: [
    {
      // A black rock in the sea, high and alone, the foam climbing its walls; a cave's mouth up top. A small boat comes
      // in through the dawn's fog (the hero, Terrón and Ulla at the bow); behind, still far, dozens of the clan's boats.
      kind: 'scene',
      id: 'cine',
      lines: [],
      cine: {
        video: 'senda-barbarian-5',
        place: 'rocaSea',
        sky: 'dawn',
        weather: 'fog',
        cam: [
          { from: [-3.85, 12.7, 1.7], at: [6.35, -12.7, 7.5], ms: 6000 },
          { from: [-1.67, 8.55, 0.9], at: [-2.66, 5.31, 0.3], ms: 5600 },
        ],
        figures: [
          { who: 'hero', q: -2.6, r: 5.2, facing: 4.5, pose: 'stand' },
          { who: 'ulla', q: -2.03, r: 4.97, facing: 4.4, pose: 'stand' },
          { who: 'terron', q: -3.23, r: 5.66, facing: 4.6, pose: 'stand' },
          { who: 'sten', q: -3.06, r: 6.12, facing: 4.5, pose: 'sit' },
        ],
      },
    },
    {
      // At the rock's foot, on the first ledge over the water, the small boat tied below.
      kind: 'scene',
      id: 'open',
      lines: [ln('l1', 'ulla'), ln('l2', 'ulla'), ln('l3', 'sten'), ln('l4', 'ulla')],
      cine: {
        place: 'rocaWall',
        sky: 'dawn',
        weather: 'fog',
        cam: [
          { from: [-9.72, 14.43, 1.6], at: [-9.91, 9.82, 0.6], ms: 9000 },
          { from: [-9.12, 12.24, 1.1], at: [-10.27, 9.93, 0.7], ms: 9000 },
        ],
        figures: [
          { who: 'ulla', q: -10.47, r: 9.93, facing: 0.4, pose: 'stand' },
          { who: 'hero', q: -9.67, r: 9.93, facing: 3.2, pose: 'stand' },
          { who: 'terron', q: -9.02, r: 10.05, facing: 3.0, pose: 'stand' },
          { who: 'sten', q: -9.89, r: 9.58, facing: 4.6, pose: 'stand' },
        ],
      },
    },
    {
      // The climb: up the ledges of the wet wall, the sea breaking below, the crows' nests; the pack comes down (§3, act 1).
      kind: 'fight',
      id: 'climb',
      mode: 'climb',
      place: 'rocaWall',
      weather: 'wind',
      budgetBp: 12500,
      goal: { kind: 'climb' },
      npcs: [
        { id: 'ulla', does: 'follow', hits: 30, follow: 1.6 },
        { id: 'terron', does: 'follow', hits: 24, safe: true, vital: false, follow: 2.4 },
      ],
      says: [
        { on: 'start', who: 'ulla', line: 'l1', delay: sec(2) },
        { on: 'cue', cue: 'band1', who: 'ulla', line: 'l2', delay: sec(1) },
        { on: 'npcHit', npc: 'ulla', who: 'ulla', line: 'l3' },
        { on: 'cue', cue: 'rear1', who: 'ulla', line: 'l4' },
        { on: 'cue', cue: 'horn', who: 'ulla', line: 'l5' },
        { on: 'cue', cue: 'ledge4', who: 'ulla', line: 'l6', delay: sec(1) },
        { on: 'cue', cue: 'ledge4', who: 'ulla', line: 'l7', delay: sec(6) },
      ],
    },
    {
      // The mouths of the rock: inside, Ivar hurt against a wall with two of his; the Wolf gone down a mouth (§4).
      kind: 'fight',
      id: 'mouths',
      mode: 'mouths',
      place: 'rocaHall',
      budgetBp: 12500,
      goal: { kind: 'mouths' },
      npcs: [
        { id: 'ulla', does: 'follow', hits: 30, follow: 1.8 },
        { id: 'terron', does: 'follow', hits: 24, safe: true, vital: false, follow: 2.6 },
        { id: 'ivar', does: 'wait', hits: 30, safe: true, vital: false },
        { id: 'hunter', does: 'wait', hits: 30, safe: true, vital: false },
      ],
      says: [
        { on: 'start', who: 'ulla', line: 'l1', delay: sec(2) },
        { on: 'at', t: sec(7), who: 'ivar', line: 'l2' },
        { on: 'at', t: sec(10), who: 'ulla', line: 'l3' },
        { on: 'at', t: sec(16), who: 'ulla', line: 'l4' },
        { on: 'cue', cue: 'crack1', who: 'ulla', line: 'l5' },
        { on: 'cue', cue: 'fall1', who: 'ulla', line: 'l6', delay: sec(1) },
        { on: 'cue', cue: 'cubs1', who: 'ivar', line: 'l9' },
        { on: 'cue', cue: 'bareLost', who: 'ulla', line: 'l7', delay: sec(1) },
        { on: 'cue', cue: 'two', who: 'ulla', line: 'l8', delay: sec(2) },
      ],
      keep: ['shut', 'bare'],
    },
    {
      // At the cave's mouth, the sea far below: Ulla, and Ivar with his wound tied.
      kind: 'fire',
      id: 'fire',
      with: ['ulla', 'ivar'],
      questions: [
        { id: 'ivarKnew', lines: [ln('l1', 'ivar'), ln('l2', 'ivar')], set: ['askedIvarKnew'] },
        {
          id: 'ragna',
          if: { flag: '@ragna.end' },
          lines: [ln('l1', 'ulla', { flag: '@ragna.home' }), ln('l2', 'ulla', { flag: '@ragna.slain' }), ln('l3', 'ulla', { flag: '@ragna.felled' })],
          set: ['askedRagna'],
        },
        { id: 'inside', lines: [ln('l1', 'ulla')], set: ['askedInside'] },
      ],
      cine: {
        place: 'rocaMouth',
        sky: 'dusk',
        cam: [
          { from: [5.92, -4.85, 8.2], at: [4.5, -9.0, 5.8], ms: 9000 },
          { from: [0.0, -6.0, 7.6], at: [5.2, -9.24, 6.0], ms: 9000 },
        ],
        figures: [
          { who: 'ulla', q: 3.56, r: -9.12, facing: 0.3, pose: 'sit' },
          { who: 'ivar', q: 5.72, r: -9.24, facing: 3.4, pose: 'sit' },
          { who: 'hero', q: 4.54, r: -8.08, facing: 4.6, pose: 'sit' },
          { who: 'terron', q: 3.2, r: -8.2, facing: 0.6, pose: 'lie' },
        ],
        fire: { q: 4.39, r: -8.78 },
      },
    },
    {
      // The ash bed: down through the cave, a low roof, grey ash on the floor like dirty snow; the cubs (§3, act 3).
      kind: 'fight',
      id: 'bed',
      mode: 'ashbed',
      place: 'rocaBed',
      budgetBp: 12500,
      goal: { kind: 'bed' },
      npcs: [
        { id: 'ulla', does: 'follow', hits: 30, safe: true, vital: false, follow: 1.6 },
        { id: 'ivar', does: 'follow', hits: 30, safe: true, vital: false, follow: 2.6 },
        { id: 'sten', does: 'follow', hits: 24, safe: true, vital: false, follow: 3.2 },
        { id: 'terron', does: 'follow', hits: 24, safe: true, vital: false, follow: 2.0 },
      ],
      says: [
        { on: 'start', who: 'ulla', line: 'l1', delay: sec(2) },
        { on: 'cue', cue: 'room1', who: 'sten', line: 'l2', delay: sec(1) },
        { on: 'cue', cue: 'open1', who: 'ulla', line: 'l3' },
        { on: 'cue', cue: 'room3', who: 'ivar', line: 'l4', delay: sec(1) },
      ],
    },
    {
      // The bottom: in the ash bed a small carved wooden boar, worn smooth from being held.
      kind: 'scene',
      id: 'bottom',
      lines: [ln('l1', 'narrator'), ln('l2', 'ulla'), ln('l3', 'ivar'), ln('l4', 'sten'), ln('l5', 'narrator'), ln('l6', 'narrator')],
      cine: {
        place: 'rocaBed',
        sky: 'night',
        cam: [
          { from: [5.54, -4.27, 1.6], at: [5.54, -7.27, 0.2], ms: 9000 },
          { from: [3.94, -5.89, 0.8], at: [5.7, -7.39, 0.1], ms: 9000 },
        ],
        figures: [
          { who: 'hero', q: 5.56, r: -6.93, facing: 4.2, pose: 'kneel' },
          { who: 'ulla', q: 4.03, r: -6.47, facing: 5.6, pose: 'stand' },
          { who: 'ivar', q: 6.18, r: -6.35, facing: 3.4, pose: 'stand' },
          { who: 'sten', q: 4.43, r: -5.66, facing: 4.6, pose: 'stand' },
          { who: 'terron', q: 6.63, r: -7.85, facing: 2.8, pose: 'stand' },
        ],
      },
    },
    {
      // The speech (docs/SENDAS.md, emotion, piece 7): at the den's mouth to the sea, the clan's boats below in sight.
      kind: 'choice',
      id: 'speech',
      lines: [
        ln('l1', 'narrator'),
        ln('l2', 'ulla'),
        ln('l3', 'narrator'),
        ln('l4', 'ulla'),
        ln('l5', 'ulla'),
        ln('l6', 'ivar'),
        ln('l7', 'ulla'),
        ln('l8', 'ulla'),
        { id: 'l9', who: 'narrator', cue: 'horns' },
        ln('l10', 'sten'),
        ln('l11', 'ulla'),
      ],
      options: [
        { id: 'neverAlone', set: ['speechNeverAlone'] },
        { id: 'singTrue', set: ['speechSingTrue'] },
      ],
      cine: {
        place: 'rocaDen',
        sky: 'dawn',
        cam: [
          // From behind the clan's people in the den, Ulla against the mouth on the sea; then over the sill, down at the
          // boats; then back on her from outside, the horns answering.
          { from: [-1.15, 8.89, 1.8], at: [1.83, 5.54, 1.0], ms: 12000 },
          { from: [0.6, 6.0, 3.0], at: [8.5, 6.0, -5.0], ms: 14000 },
          { from: [4.17, 6.47, 0.6], at: [1.3, 6.0, 1.2], ms: 14000 },
        ],
        figures: [
          { who: 'ulla', q: 1.3, r: 6.0, facing: 0, pose: 'stand' },
          { who: 'hero', q: 0.27, r: 6.47, facing: 0.2, pose: 'stand' },
          { who: 'sten', q: -0.68, r: 7.16, facing: 0.4, pose: 'stand' },
          { who: 'ivar', q: -0.34, r: 5.08, facing: 0.1, pose: 'sit' },
          { who: 'terron', q: -0.4, r: 6.81, facing: 0.3, pose: 'stand' },
        ],
      },
    },
    {
      // The Grey Wolf (§5): in the den among the cubs, up the roof's crack to the summit, the last run.
      kind: 'fight',
      id: 'wolf',
      mode: 'wolf',
      place: 'rocaDen',
      weather: 'wind',
      budgetBp: 12500,
      goal: { kind: 'wolf' },
      npcs: [
        { id: 'ulla', does: 'wait', hits: 30, safe: true, vital: false },
        { id: 'ivar', does: 'wait', hits: 30, safe: true, vital: false },
        { id: 'sten', does: 'wait', hits: 24, safe: true, vital: false },
        { id: 'skiff', does: 'wait', hits: 15, safe: true, prop: true },
      ],
      says: [
        { on: 'start', who: 'sten', line: 'l1', delay: sec(2) },
        { on: 'cue', cue: 'look1', who: 'ulla', line: 'l2' },
        { on: 'cue', cue: 'dive1', who: 'ulla', line: 'l3', delay: sec(1) },
        { on: 'cue', cue: 'cut1', who: 'ulla', line: 'l4' },
        { on: 'cue', cue: 'rear1', who: 'ulla', line: 'l5' },
        { on: 'cue', cue: 'up', who: 'ivar', line: 'l6', delay: sec(1) },
        { on: 'cue', cue: 'boats1', who: 'ulla', line: 'l7', delay: sec(1) },
        { on: 'cue', cue: 'ivarIn', who: 'ivar', line: 'l8', if: TAKEN },
        { on: 'cue', cue: 'ivarIn', who: 'ivar', line: 'l9', if: LEFT },
        { on: 'cue', cue: 'last', who: 'ulla', line: 'l10', delay: sec(1) },
        { on: 'cue', cue: 'boatRun1', who: 'sten', line: 'l11' },
      ],
      foe: true,
      trial: true,
    },
    {
      // On the rock, the Wolf fallen: its ash goes with the wind over the sea, the sun out between the clouds, the horns.
      kind: 'scene',
      id: 'close',
      lines: [
        ln('l1', 'narrator'),
        ln('l2', 'ulla'),
        ln('l3', 'ivar'),
        ln('l4', 'ivar', TAKEN),
        ln('l5', 'narrator', TAKEN),
        ln('l6', 'ulla', TAKEN),
        ln('l7', 'ivar', LEFT),
        ln('l8', 'ulla', LEFT),
      ],
      cine: {
        place: 'rocaTop',
        sky: 'day',
        weather: 'wind',
        cam: [
          { from: [5.4, -2.6, 6.6], at: [3.81, -7.62, 4.6], ms: 10000 },
          { from: [0.97, -4.27, 5.6], at: [6.28, -10.97, 4.8], ms: 10000 },
        ],
        figures: [
          { who: 'hero', q: 3.58, r: -7.16, facing: 4.6, pose: 'stand' },
          { who: 'ulla', q: 5.43, r: -7.85, facing: 3.4, pose: 'stand' },
          { who: 'ivar', q: 2.24, r: -7.27, facing: 0.4, pose: 'stand' },
          { who: 'terron', q: 4.05, r: -6.7, facing: 4.0, pose: 'stand' },
        ],
      },
    },
    {
      // The way back (the loss): the clan's boats go home past the White Island; on its shore by the stone of the
      // strokes, Bera, small, a fire lit. She doesn't get in.
      kind: 'scene',
      id: 'sea',
      lines: [ln('l1', 'narrator'), ln('l2', 'sten'), ln('l3', 'bera'), ln('l4', 'bera', { flag: 'askedWhyBera' }), ln('l5', 'narrator')],
      cine: {
        place: 'rocaIsla',
        sky: 'day',
        cam: [
          { from: [-8.35, 12.7, 1.6], at: [0.87, -1.73, 0.8], ms: 10000 },
          { from: [-2.59, 8.78, 0.8], at: [0.69, -1.39, 0.7], ms: 10000 },
        ],
        figures: [
          { who: 'bera', q: 1.22, r: -1.85, facing: 1.5, pose: 'sit' },
          { who: 'sten', q: -3.88, r: 6.16, facing: 4.5, pose: 'stand' },
          { who: 'hero', q: -2.68, r: 6.16, facing: 4.5, pose: 'stand' },
          { who: 'terron', q: -1.88, r: 6.16, facing: 4.5, pose: 'stand' },
        ],
        fire: { q: 0.68, r: -1.96 },
      },
    },
    {
      // The tail by night (the camp up again): the carts unloaded, the herd back in the corrals, the whole clan on the
      // grass round a great fire, singing.
      kind: 'scene',
      id: 'epilogue',
      lines: [
        ln('l1', 'narrator'),
        ln('l2', 'ulla'),
        ln('l3', 'ulla'),
        ln('l4', 'narrator'),
        ln('l5', 'narrator', { flag: 'speechNeverAlone' }),
        ln('l6', 'narrator', { flag: 'speechSingTrue' }),
        ln('l7', 'ulla', { flag: 'askedLastWords' }),
        ln('l8', 'sten', { flag: 'askedBeraWant' }),
        ln('l12', 'narrator', TAKEN),
        ln('l13', 'narrator', LEFT),
        ln('l9', 'ulla', { flag: '@ragna.end' }),
        ln('l10', 'ulla'),
        ln('l11', 'narrator'),
      ],
      cine: {
        video: 'senda-barbarian-5-end',
        place: 'cazaFeast',
        sky: 'night',
        cam: [
          { from: [3.4, 4.6, 2.4], at: [0.0, 0.0, 0.6], ms: 11000 },
          { from: [-2.6, 2.8, 1.2], at: [0.4, -0.4, 0.8], ms: 11000 },
        ],
        figures: [
          { who: 'ulla', q: 0, r: -2, facing: 1.5, pose: 'stand' },
          { who: 'ivar', q: 2, r: -2, facing: 2.6, pose: 'sit' },
          { who: 'sten', q: 2, r: 0, facing: 3.4, pose: 'sit' },
          { who: 'hero', q: -1, r: 2, facing: 5.4, pose: 'sit' },
          { who: 'terron', q: -2, r: 2, facing: 0.2, pose: 'lie' },
          { who: 'rower1', q: -2, r: 0, facing: 0.0, pose: 'sit' },
          { who: 'rower2', q: -2, r: 1, facing: 0.2, pose: 'sit' },
          { who: 'rower3', q: 1, r: 2, facing: 4.6, pose: 'sit' },
          { who: 'rower4', q: 0, r: 2, facing: 4.8, pose: 'sit' },
          { who: 'hunter', q: 3, r: -1, facing: 3.0, pose: 'stand' },
          { who: 'hunter2', q: -3, r: 1, facing: 0.0, pose: 'stand' },
        ],
        fire: { q: 0, r: 0 },
      },
    },
  ],
};
